import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const AdminContext = createContext(null);
const TOKEN_KEY = "dc_admin_token";
export const apiError = (error, fallback = "Something went wrong. Please try again.") => {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(" ");
    return fallback;
};

export const AdminSession = ({ children }) => {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [status, setStatus] = useState(token ? "checking" : "signed-out");
    const [notice, setNotice] = useState("");
    const [attempt, setAttempt] = useState(0);
    const api = useMemo(() => axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, timeout: 30000, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);
    const logout = useCallback((expired = false) => {
        sessionStorage.removeItem(TOKEN_KEY); setToken(""); setStatus("signed-out");
        setNotice(expired ? "Your session has expired. Please sign in again." : "");
    }, []);
    useEffect(() => {
        if (!token) return;
        const controller = new AbortController();
        const interceptor = api.interceptors.response.use((response) => response, (error) => {
            if (error.response?.status === 401) logout(true);
            return Promise.reject(error);
        });
        setStatus("checking");
        api.get("/admin/session", { signal: controller.signal }).then(() => setStatus("ready")).catch((error) => {
            if (!axios.isCancel(error) && error.response?.status !== 401) setStatus("error");
        });
        return () => { controller.abort(); api.interceptors.response.eject(interceptor); };
    }, [api, token, logout, attempt]);
    const login = async (credentials) => {
        const { data } = await api.post("/admin/login", credentials);
        sessionStorage.setItem(TOKEN_KEY, data.token); setNotice(""); setToken(data.token);
    };
    return <AdminContext.Provider value={{ api, token, status, notice, login, logout, retry: () => setAttempt((value) => value + 1) }}>{children}</AdminContext.Provider>;
};

export const useAdmin = () => useContext(AdminContext);